import { useState } from 'react'

import { useStoreAnalytics } from '@/hooks/features/analytics'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { titleClass } from '../constants'
import { LowSample, type AnalyticsFilters } from '../parts'

import type { AnalyticsBreakdownRow, AnalyticsTraffic } from '@/types/analytics'

const CHANNEL_LABELS: Record<string, string> = {
  direct: 'Direct',
  search: 'Search',
  social: 'Social',
  paid: 'Paid ads',
  email: 'Email',
  referral: 'Other sites',
}

type TrafficView = 'channels' | 'sources' | 'devices' | 'landing_pages'

/** Rows of a traffic breakdown: sessions, purchases and conversion. */
const BreakdownTable = ({ rows, label, format = (v) => v }: { rows: AnalyticsBreakdownRow[]; label: string; format?: (value: string) => string }) =>
  !rows.length ? (
    <p className="py-6 text-center text-sm text-muted-foreground">No visits in these dates.</p>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{label}</TableHead>
          <TableHead className="text-right">Sessions</TableHead>
          <TableHead className="text-right">Orders</TableHead>
          <TableHead className="text-right">Conversion</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.value}>
            <TableCell className="max-w-64 truncate" title={r.value}>{format(r.value)}</TableCell>
            <TableCell className="text-right tabular-nums">{r.sessions.toLocaleString()}</TableCell>
            <TableCell className="text-right tabular-nums">{r.purchase_sessions.toLocaleString()}</TableCell>
            <TableCell className="text-right tabular-nums">
              {r.conversion_rate}%
              <LowSample show={r.low_sample} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

/** Where visitors come from: channels, sources, devices and the pages they land on, each with its conversion. */
export const TrafficTab = ({ filters }: { filters: AnalyticsFilters }) => {
  const [view, setView] = useState<TrafficView>('channels')
  const traffic = useStoreAnalytics<AnalyticsTraffic>('traffic', filters)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className={titleClass}>Where visitors come from</CardTitle>
        <Tabs value={view} onValueChange={(v) => setView(v as TrafficView)}>
          <TabsList>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="sources">Sources</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="landing_pages">Landing pages</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        {traffic.isLoading ? (
          <Skeleton className="h-48" />
        ) : traffic.isError ? (
          <p className="text-sm text-error">{traffic.error.message}</p>
        ) : traffic.data ? (
          <BreakdownTable
            rows={traffic.data[view]}
            label={{ channels: 'Channel', sources: 'Source', devices: 'Device', landing_pages: 'Landing page' }[view]}
            format={view === 'channels' ? (v) => CHANNEL_LABELS[v] ?? v : view === 'devices' ? (v) => v.replace(/^\w/, (c) => c.toUpperCase()) : undefined}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}
